import React, { useState } from 'react'
import { Line } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  Title,
  Tooltip,
  Legend,
  CategoryScale,
} from 'chart.js'
import 'chartjs-adapter-date-fns'

import './style.scss'
import { format } from "date-fns";
import { Status, statusColors, statusLabels } from "../../utils/utils";

ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  Title,
  Tooltip,
  Legend,
  CategoryScale
)

type DocumentItem = {
  docNumber: string
  requestDate: string
}


type DocumentStats = Record<Status, DocumentItem[]>

type Props = {
  data: Record<string, DocumentStats>
}

// fonction utilitaire pour grouper par unité
const groupByTimeUnit = (
  items: { date: number; status: string }[],
  unit: 'day' | 'month' | 'year'
) => {
  const grouped: Record<string, number> = {}
  items.forEach((item) => {
    const key = format(item.date, unit === 'day' ? 'yyyy-MM-dd' : unit === 'month' ? 'yyyy-MM' : 'yyyy')
    grouped[key] = (grouped[key] || 0) + 1
  })

  // transformer en points {x, y}
  return Object.entries(grouped).map(([key, count]) => ({
    x: new Date(key).getTime(),
    y: count,
  }))
}

const DocumentEvolutionChart: React.FC<Props> = ({ data }) => {
  const [timeUnit, setTimeUnit] = useState<'day' | 'month' | 'year'>('month')

  const allItems: { date: number; status: Status }[] = []
  Object.keys(data).forEach((docType) => {
    ;(Object.keys(statusLabels) as Status[]).forEach((status) => {
      data[docType][status].forEach((item) => {
        allItems.push({
          date: new Date(item.requestDate).getTime(),
          status,
        })
      })
    })
  })

  const datasets = (Object.keys(statusLabels) as Status[]).map((status) => {
    const points = groupByTimeUnit(
      allItems.filter((i) => i.status === status),
      timeUnit
    )

    return {
      label: statusLabels[status],
      data: points.sort((a, b) => a.x - b.x),
      borderColor: statusColors[status],
      backgroundColor: statusColors[status],
      fill: false,
      tension: 0.2, // légère courbe
    }
  })

  return (
    <div className="DocumentEvolutionChart">
      {/* Sélecteur */}
      <div className="controls">
        <h4> Évolution des vérifications plus détaillées, triée par :</h4>
        <select
          id="unit"
          value={timeUnit}
          onChange={(e) => setTimeUnit(e.target.value as any)}
        >
          <option value="day">Jour</option>
          <option value="month">Mois</option>
          <option value="year">Année</option>
        </select>
      </div>

      {/* Graphe */}
      <div className="chart-container">
        <Line
          data={{ datasets }}
          options={{
            responsive: true,
            plugins: {
              legend: { position: 'top' },
              tooltip: {
                mode: 'nearest',
                intersect: false,
                callbacks: {
                  label: (ctx) => {
                    const date = new Date(ctx.parsed.x as number)
                    return `${ctx.dataset.label}: ${ctx.parsed.y} vérifications • ${date.toLocaleDateString()}`
                  },
                },
              },
            },
            scales: {
              x: {
                type: 'time',
                time: {
                  unit: timeUnit,
                  tooltipFormat: 'PP',
                  displayFormats: {
                    day: 'dd/MM/yyyy',
                    month: 'MM/yyyy',
                    year: 'yyyy',
                  },
                },
                title: { display: true, text: 'Date' },
              },
              y: {
                beginAtZero: true,
                title: { display: true, text: 'Nombre de vérifications' },
              },
            },
          }}
        />
      </div>
    </div>
  )
}

export default DocumentEvolutionChart
